import EE, { EventEmitter } from '@src/common/event';
import agentStore from '@/store/agentStorage';
import { isStringEmpty } from '@shared/utils';
import { AssistantType } from '@shared/constants/chat';
import {
  ABORT_MESSAGE,
  CHAT_ERROR_MESSAGE,
  checkFEMessageId,
  createChatMessage,
  createClearContextMessage,
  createResumeMessage,
  createWelcomeMessage,
  generateCustomAPIParams,
  getAnnexMsgId,
  getFailMessageParams,
  getFixedReferSearch,
  getFixedVideoWatch,
  getKwargs,
  getMessageLogData,
  MessageType,
  NETWORK_ERROR_MESSAGE,
  persistMessage,
  removeChatStorageItem,
  RESPONSE_ERROR_MESSAGE,
  SERVICE_ERROR_MESSAGE,
  UNKNOW_EXCEPTION_MESSASGE,
} from '@shared/store/chat';
import { HelperCore } from '@/common/helper';
import {
  abortEventStream,
  createCompletion,
  getChatHistory,
  getChatHeaderInfo,
  removeMessage,
  removeAllMessages,
  clearContext,
  getAgentInfo,
  getUnLoginedAgentInfo,
} from '@/services/chat';
import { makeAutoObservable, runInAction, toJS } from 'mobx';
import navbarStore from '../navbar';
import modelChoiceStore from '@/components/model-choice/store';
import globalStore from '../global';
import StepsLogger from "@public/log/steps-logger";
import appLogger from '@public/log/monitor';
import userStore from '../user';
import Loader from '@/common/helper/loader';
import client from '../client';
import slsLogger from '@public/log/sls';
import BanModal from '@/components/modal/ban';
import inputTypeStore from '@/components/input-file/input-type';
import Conf from '@/common/conf';
import { request } from '@/common';
import Other from '@/common/helper/other';
import localRobot from '@/common/localRobot.js';
import { sendBroadcast } from '@/hooks/useBroadcast';
import apiRobot from '@/common/apiRobot';
import chatStoreManager from './manager';
import { isEmpty } from 'lodash-es';
import fileStore from './fileStore';
import modelHistoryStore from '@/components/navbar-v2/model-history/store';
import aigentHistoryStore from '@/components/navbar-v2/aigent-history/store';
import cacheStore from '../cache';
import McpStore from './mcpStore';
import { McpAgentType, AutoPreviewAttachmentTypes, McpToolsConfig, getAttachmentsId, McpActionTypes, McpActionTools } from '@shared/constants/mcp';
import serverChoiceStore from '@/components/server-choice/store';
import noticeStore from '../notice';
import createDebug from '@/common/create-debug';
import limitAlertStore from '@/components/agent-limit/store';
import urlStore from './urlStore';

const debugChat = createDebug("chat");

const INITIAL_HISTORY_DATA = {
  chat_type: 10,
  conversation_id: '',
  title: '',
  messages: [],
  watermark: '',
  npt: '',
};

class ChatStore {
  #chatController = null;

  /**
   * 新对话由服务器生成返回的 ID
   * 只在产生新对话时临时记录使用
   * 不能直接做为对话 ID 使用
   */
  #issuedChatId = '';
  chatId = '';

  #emitter = new EventEmitter();

  summaryPrompt = '总结此页面';

  currAsstId = '';

  chatHeaderInfo = null;
  agentInfo = null; // 智能体的详细信息
  agentInfoByHistory = null;  // 会话历史中返回的 agentInfo(简版)
  agentId = null;

  aiAgentId = null; // Mcp 智能体
  aiAgentFlowId = null; // 工作流智能体

  isLoadingHistory = false;
  isLoadingHeaderInfo = false;

  isAITyping = false;
  canAborting = false;
  loadingVideoMindMap = false;

  messageMap = new Map();
  __historyData = {
    ...INITIAL_HISTORY_DATA,
  };

  file = null

  queueCount = 0; // 排队人数
  timerQueue = null;
  timeQueueEmptyContent = 30000;  // 与服务端确认，基本没有这个 case，修改轮询时间 10s -> 30s
  timeQueueRoll = 3000;
  hasOrderQueueNotice = false;

  // 手动二次生成agent
  manuGenAgentSet = {};

  get messageList() {
    const list = Array.from(toJS(this.messageMap).values());
    return list;
  }

  get firstMessage() {
    const list = this.messageList;
    if (list.length > 0) {
      return list[0];
    }
    return null;
  }

  get lastMessage() {
    const list = this.messageList;
    if (list.length > 0) {
      return list[list.length - 1];
    }
    return null;
  }

  get lastFinishedMessage () {
    const list = [];
    Array.from(toJS(this.messageMap).keys()).forEach(messageId => {
      if(!checkFEMessageId(messageId)){
        list.push(this.messageMap.get(messageId));
      }
    })
    if(!list.length){
      return null;
    }
    return HelperCore.copyObj(list[list.length - 1]);
  }

  /**
   * 一期知识库不可切换
   * 取第一条消息的 dataset
   */
  get conversationDataset () {
    const messageItem = this.messageList.find(item => item.dataset);
    if(messageItem){
      return {
        dataset : HelperCore.copyObj(messageItem.dataset),
      }
    }
    return {};
  }

  get historyData() {
    const { title } = this.__historyData;
    return {
      ...this.__historyData,
      conversation_id: this.chatId,
      title: isStringEmpty(title)
        ? this.firstMessage?.prompt || Loader.defaultChatTilte
        : title,
      message_list: this.messageList,
    };
  }

  get isOwner () {
    return this.historyData.qid == userStore.uid;
  }

  get isOfficialAsstChat() {
    return this.historyData.chat_type == AssistantType.Official;
  }

  get isChatModelChat() {
    return this.historyData.chat_type == AssistantType.ChatModel;
  }
  // 文生图模型
  get isTtiModel() {
    return navbarStore.chatModels[this.currAsstId]?.conversation_type ==  1;
  }

  get lastChatType () {
    const lastMessage = this.lastMessage;
    if(lastMessage?.page?.length){
      return "summaryweb";
    }
    return "free_chat";
  }

  get isAgent () {
    return this.agentId != null;
  }

  get agentSystemInfo () {
    return this.agentInfo?.params?.system_info ?? {};
  }

  /**
   * 是否运营 Agent
   */
  get isRecommendAgent () {
    return this.agentInfo?.agent_type == "public"
      || this.agentInfoByHistory?.agent_type == "public"
  }

  /**
   * 会话是否绑定了知识库
   * Web 实现: 会话中不可切换、移除知识库
   * App 实现: 会话中可能切换知识库
   *
   * 前期数据格式兼容，最早知识库是按会话记录的
   * 杨宇处理后，可删除
   */
  get hasMessageWithKnowledge () {
    return !isEmpty(this.historyData.dataset) || this.messageList.find(item => !isEmpty(toJS(item.dataset)));
  }
  /**
   * 最后一条消息的知识库
   */
  get lastKnowledge () {
    if(!this.lastMessage){
      return null;
    }
    return this.lastMessage.dataset;
  }
  get lastKnowledgeKsId () {
    if(!this.lastMessage){
      return null;
    }
    return this.lastMessage.ks_id;
  }

  mcpStore = null;

  /**
   * 当前的模型信息
   * 区分
   * 1. 模型
   * 2. 智能体
   * 3. 老版智能体
   *
   * 补全 icon/title 字段，用于显示
   */
  get modelInfo () {
    let modelInfo = null;

    // 老版智能体
    if(this.isAgent){
      try{
        modelInfo = {
          ...this.agentInfo,
          ...{
            icon : this.agentInfo.params.system_info.icon,
            title : this.agentInfo.params.system_info.name,
          }
        }
      } catch(e){
        modelInfo = this.agentInfo;
      }
    }
    else if(this.aiAgentId){
      modelInfo = this.mcpStore.agentInfo;
    }
    else if(this.currAsstId){
      modelInfo = navbarStore.chatModels[this.currAsstId];
    }
    if(modelInfo && !modelInfo.icon){
      modelInfo.icon = Loader.defaultModelIcon;
    }
    return modelInfo;
  }

  constructor({
    type = AssistantType.ChatModel,
    id = undefined,
    asstId = '',
    input = '',
    agentId,
    agentInfo,
    aiAgentId,
    aiAgentFlowId,
    knModel,
  } = {}) {
    makeAutoObservable(this);

    this.chatId = id;
    this.currAsstId = asstId;
    this.agentId = agentId;
    this.agentInfo = agentInfo;
    this.aiAgentId = aiAgentId;
    this.aiAgentFlowId = aiAgentFlowId;
    this.__historyData.chat_type = type;
    this.mcpStore = new McpStore(this);
    if(aiAgentId){
      this.mcpStore.setAgentID(aiAgentId);
    }
    if(aiAgentFlowId){
      this.mcpStore.setAgentID(aiAgentFlowId);
    }
    if(knModel){
      this.__historyData.kn_custom_model = knModel;
    }

    if (this.isOfficialAsstChat || !input) {
      if(agentId && agentInfo?.params?.system_info?.greeting_message) {
        let { greeting_message } = agentInfo.params.system_info
        let message = createWelcomeMessage(agentId, greeting_message);
        message = {...message, ...agentInfo.params.system_info, agentId}
        delete message.role_id
        this.messageMap.set(message.msg_id, message);
      }else {
        if(globalStore.isKownledgeChat()){
          return;
        }
        const model = navbarStore.chatModels[this.currAsstId];
        if(!model){
          return;
        }
        const { id, description, greeting } = model;
        const message = createWelcomeMessage(id, greeting || description);
        this.messageMap.set(message.msg_id, message);
      }
    }
  }

  resetState = () => {
    this.chatId = '';
    this.isAITyping = false;
    this.canAborting = false;
    this.messageMap = new Map();
    this.__historyData = { ...INITIAL_HISTORY_DATA };
  };

  setAITyping = value => {
    this.isAITyping = value;

    client.run("NotifyTyping", null, value ? "1" : "0");
  };
  setCurrAsstId = value => (this.currAsstId = value);

  newChat = () => {
    this.resetState();
  };
  // entrance //0.默认,1.知识库
  fetchChatHistory = async (id, kn_continue_chat, entrance = 0, ks_id, fold_id, file_id) => {
    if (isStringEmpty(id) || this.isLoadingHistory || this.isAITyping) {
      return;
    }
    entrance = Number(entrance || 0);
    this.isLoadingHistory = true;

    try {
      const npt = this.historyData.npt;
      const { code, data } = await getChatHistory({
        conversation_id: id,
        page_size: 10,
        npt,
        ...(kn_continue_chat ? kn_continue_chat:{}),
        entrance,
        ks_id,
        fold_id,
        file_id,
      });
      // const { code, data } = mockData;
      if (code == 0) {
        // debug 智能体类型变更
        // if(HelperCore.IS_LOCAL){
        //   data.ai_agent_type = 1;
        // }

        const {
          conversation_id,
          messages,
          agent_id,
          agent_info,
          ai_agent_id,
          ai_agent_info,
          ai_agent_flow_id,
          ai_agent_flow_info,
          ...resetData
        } = data;
        runInAction(() => {
          this.agentInfoByHistory = agent_info;
        });

        // 接口取完整的 agentInfo
        if(this.agentInfo?.agent_id != agent_id) {
          (userStore.isUser ? getAgentInfo : getUnLoginedAgentInfo)({ agent_id, query_type: 'all' }).then(
            res => {
              runInAction(() => {
                if(res?.data?.data?.agent_id) {
                  this.agentId = agent_id;
                  this.agentInfo = res.data.data;
                }
              })
            }
          ).catch(err=>console.log(err));
        }
        // Mcp 智能体
        if(ai_agent_id){
          if(entrance == 1 && (ai_agent_info?.agent_type == McpAgentType.normal || ai_agent_info?.agent_type == McpAgentType.simple)){// 从知识库进入,并且带了mcp技能，需要跳回放页面
            location.href = `/share/mcp?cid=${conversation_id}`;
            return
          }
          runInAction(() => {
            this.aiAgentId = ai_agent_id;
            this.mcpStore.setAgentID(ai_agent_id, ai_agent_info && typeof ai_agent_info == "object" && !Array.isArray(ai_agent_info) ? ai_agent_info : null);
          });
        }
        // 工作流智能体
        if(ai_agent_flow_id){
          runInAction(() => {
            this.aiAgentFlowId = ai_agent_flow_id;
            this.mcpStore.setAgentID(ai_agent_flow_id, ai_agent_flow_info && typeof ai_agent_flow_info == "object" && !Array.isArray(ai_agent_flow_info) ? ai_agent_flow_info : null);
          });
        }
        runInAction(() => {
          this.chatId = conversation_id;
          this.__historyData = { ...resetData };
        });
        const messageList = Array.isArray(messages) ? messages.reverse() : [];
        if (messageList.length > 0) {
          const messageMap = new Map();
          for (const [index, item] of messageList.entries()) {
            // 后续不再有会话内切模型
            runInAction(() => {
              this.currAsstId = this.currAsstId || item.role_id;
            });

            // debug 知识库召回
            // if(HelperCore.IS_LOCAL){
            //   item.user_knowledge = [
            //     {
            //       "folder_id":"1",
            //       "file_id":"1",
            //       "file_name":"pdf.PDF",
            //     },
            //     {
            //       "folder_id":"1",
            //       "file_id":"2",
            //       "file_name":"word.docx",
            //     },
            //     {
            //       "folder_id":"1",
            //       "file_id":"3",
            //       "file_name":"xls.xls",
            //     },
            //   ]
            // }

            // debug html 预览
            // if(HelperCore.IS_LOCAL){
            //   item.code_info = {
            //     title : "代码标题",
            //   }
            // }

            // debug prompt
            // if(HelperCore.IS_LOCAL){
            //   item.prompt = "人工智能医疗应用现状及案例2025年人工智能医疗应用现状及案例2025年人工智能医疗应用现状及案例2025年";
            // }

            // debug 断点续传
            // if(HelperCore.IS_LOCAL){
            //   item.is_doing = 1;
            // }

            messageMap.set(item.msg_id, {
              conversation_id,
              ...item,
              ...{
                refer_search: getFixedReferSearch(
                  item.refer_search,
                  item.prompt
                ),
                search_keyword : (item.search_keyword || []).filter(_ => _.content),
                file : item.file && typeof item.file == "string" ? JSON.parse(item.file) : item.file,
                video_watch : getFixedVideoWatch(item.video_watch),
                events : item.events?.length
                  ? item.events.filter(event => !this.mcpStore.isFilteredEvent(event))
                  : item.events
              },
              status: 1,
              cost : cacheStore.getCostTime(item.msg_id),
              is_history : true,
            });
          }
          if(npt){
            this.messageMap.forEach((value, key) => messageMap.set(key, value));
          }
          debugChat("fetch over");
          runInAction(() => {
            this.messageMap = messageMap;
          });

          if (this.lastMessage.is_doing != 0) {
            slsLogger.log({
              key : "chat",
              action : "retry",
              ext : JSON.stringify(this.lastMessage),
              conversation_id : this.chatId,
              message_id : this.lastMessage.msg_id,
            })

            // 有未完成的回答
            debugChat("fetch resume");
            this.sendMessage(await createResumeMessage(this.lastMessage));
            setTimeout(() => {
              this.scrollToBottom();
            }, 100);
          }
          if(
            this.aiAgentId
            || this.lastMessage.role_biz == "ai_agent"
            || this.mcpStore.isAgentFlow
            || this.lastMessage.role_biz == "ai_agent_flow"
          ){
            this.mcpStore.setPanelMessageId(this.lastMessage.msg_id);
            try{
              const events = this.lastMessage.events;
              let lastToolsId = null;
              events.forEach(item => {
                if(
                  (item.tool
                  && McpToolsConfig[item.tool]
                  && item.status == "success")
                  || [
                    ...McpActionTypes,
                    "toolNeedConfirm",
                    "extraData",
                  ].includes(item.type)
                  || McpActionTools.includes(item.tool)
                ){
                  lastToolsId = item.planStepId;
                }
              })
              if(lastToolsId){
                this.mcpStore.setShowRightPanel(true);
                this.mcpStore.setCurrentPanelId(lastToolsId);
              }
            } catch(e){}
          }
          if(this.lastMessage.kn_custom_model){
            runInAction(() => {
              this.__historyData.kn_custom_model = this.lastMessage.kn_custom_model;
            });
          }

          // 打点
          this.log({
            key: "aibot_chat",
            action: "show",
            ext: "history",
          });
        }
        // 没有有效消息的对话
        else {
          globalStore.gotoHome();
        }
      } else if (code == 100011) {
        BanModal.show();
        globalStore.gotoHome();
        return false;
      } else {
        if(globalStore.isKownledgeChat()){
          return true;
        }
        if([
          100004,   // 没有权限
          300105,   // 会话不存在
          300100,   // 包括会话不存在，也可能是会话状态错误，被删除等
        ].includes(code)){
        }
        globalStore.gotoHome(true);
        //todo
        //globalStore.gotoNotFoundPage();
        return false;
      }
    } finally {
      this.isLoadingHistory = false;
    }
    return true;
  };

  appendMessage = async data => {
    if ((this.messageMap.size > 1 || (this.messageMap.size == 1 && this.firstMessage?.msg_type != MessageType.Welcome)) && (
      !data.renew && !data.messageId && !data.compare_parent_id && (
        [
          "summary_page",   // 网页总结
          "translation",    // 翻译
          "explain",        // 解释
          "page",           // 与网页对话
          "doc",            // 文档
          "chat_image",     // 图片对话
          "extract_image",  // 图片提取文字
          "explain_image",  // 图片解释
          "page_mind",      // 网页脑图
          "mind",           // 自定义脑图
          "rewrite",        // 改写
          "summary_video",  // 视频总结
        ].includes(data.role_biz)
        || data.isSearchSummary // 搜索结果页
        || data.kwargs?.sub_process?.includes("mind") // 思维导图
      )
    )) {
      const ok = await clearContext(this.chatId);
      if(ok){
        const clrCtxMsg = createClearContextMessage(this.chatId);
        this.messageMap.set(clrCtxMsg.msg_id, clrCtxMsg);
      }
      delete(data.annex_msg_id);
    }
    if(data.renew && data.messageId == data.annex_msg_id){
      delete(data.annex_msg_id);
    }
    // 补全知识库参数
    // if(!data.dataset){
    //   Object.assign(data, this.conversationDataset);
    // }
    const chatMessage = createChatMessage(data);
    this.messageMap.set(chatMessage.msg_id, chatMessage);
    return chatMessage;
  };

  updateMessage = (messageId, message) => {
    if (this.messageMap.has(messageId)) {
      const prevMessage = this.messageMap.get(messageId);
      const nextMessage = { ...prevMessage, ...message };
      this.messageMap.set(messageId, nextMessage);
      persistMessage(nextMessage);
    }
  };

  // 更新 agentStatus 节点状态
  updateMcpAgentStatusEvent (messageId, data) {
    if (this.messageMap.has(messageId)) {
      const message = HelperCore.copyObj(this.messageMap.get(messageId));
      if(message.events?.length){
        const agentStatusEvent = message.events.find(event => event.type == "agentStatus");
        if(agentStatusEvent){
          Object.assign(agentStatusEvent, data);
        }
      }
      this.messageMap.set(messageId, message);
    }
  }
  // 更新任意唯一 type 节点状态
  updateMcpEventByType (messageId, type, data) {
    if (this.messageMap.has(messageId)) {
      const message = HelperCore.copyObj(this.messageMap.get(messageId));
      if(message.events?.length){
        const event = message.events.find(event => event.type == type);
        if(event){
          Object.assign(event, data);
        }
      }
      this.messageMap.set(messageId, message);
    }
  }
  getMcpAgentStatusEvent (msg_id) {
    const message = msg_id ? this.messageMap.get(msg_id) : this.lastMessage;
    if(!message || !message.events?.length){
      return null;
    }
    return message.events.find(event => event.type == "agentStatus");
  }

  handleIssuedMessageId = (id, message) => {
    if (this.messageMap.has(id)) {
      const current = this.messageMap.get(id);
      this.messageMap.delete(id);
      this.messageMap.set(current.msg_id, { ...current, ...message });
    }
  };

  handleSearchMessage = (messageId, searchData, id) => {
    try {
      let message = {};
      const { type, message: data } = JSON.parse(searchData);
      switch (type) {
        // AI 搜索取 search_queries
        // 智脑取 search
        case 'search':
        case 'search_queries':
          if(!data){
            break;
          }
          if(typeof data == "string" && data.includes(" ")){
            break;
          }
          const search_keyword = (Array.isArray(data) ? data : data
            .split(/\s+|,/)
            .filter(_ => _)
            .map(content => ({ content }))).filter(_ => _.content);
          if(search_keyword.length){
            message = { search_keyword };
          }
          this.stepsLogger.record('search_keyword');
          break;
        case 'search_result':
          let searchResult = data;
          if (typeof data === 'string') {
            searchResult = JSON.parse(data);
          }
          const { total, list } = searchResult;
          message = {
            search_total: total,
            refer_search: getFixedReferSearch(list, this.lastMessage?.prompt),
          };
          this.stepsLogger.record('refer_search');
          break;
        case 'stat_model': {
          // 模型类型，用于打点
          const stat_model = this.lastMessage?.stat_model || {};
          message = { stat_model: { ...stat_model, ...data } };
          break;
        }
        case 'mindmap':{
          message = { mind_map: data };
          break;
        }
        case 'reasoning_text':{
          let reasoning_text = this.messageMap.get(messageId)?.reasoning_text ?? '';
          message = { reasoning_text: reasoning_text + data };
          break;
        }
        case 'reasoning_duration':{
          message = { reasoning_duration: data };
          break;
        }
        case "is_white":{
          message = { is_white: 1 };
          break;
        }
        case "user_knowledge":{
          message = { user_knowledge: data };

          try{
            const messageItem = this.messageMap.get(messageId);

            slsLogger.log({
              key : "knowledge",
              action : "user_knowledge",
              conversation_id : this.#issuedChatId,
              message_id : messageItem.msg_id,
              query : messageItem.prompt,
              ext : JSON.stringify(data),
              ext1 : JSON.stringify(messageItem.dataset),
              ext2 : messageItem.dataset_src,
            })
          } catch(e){}
          break;
        }
        case "ai_agent":{
          if(this.aiAgentId && !this.aiAgentFlowId){
            this.orderQueue();
          }
          const messageItem = this.messageMap.get(messageId) || {};
          const events = messageItem.events || [];
          let isNewEvent = false; // 是否首次出现，按 planStepId 判断：首次出现/更新状态

          // 打点, chat_log，可用于此类需求后续通用
          if (data?.type === 'chat_log') {
            let json = null;
            try {
              json = JSON.parse(data?.content);
            } catch (err) {}
            if (json) {
              const {
                id,
                title,
              } = this.modelInfo || {};
              appLogger.log({
                key: 'aibot_chat',
                action: 'act',
                conversation_id : this.#issuedChatId,
                message_id : messageItem.msg_id,
                message_name : messageItem.prompt,
                arrt: 'category_source',
                agent_id: id,
                agent_name: title,
                ext1: json?.logObj?.site,
                ext2: '',
              });
            }
          }

          // 无效节点，直接抛弃
          if(this.mcpStore.isFilteredEvent(data)){
            HelperCore.log(`过滤无效节点`, data);
            return;
          }

          const targetEvent = events.find(item => item.planStepId == data.planStepId);

          // 流式更新
          if(["append", "replace"].includes(data.action) && data.targetKey){
            if(targetEvent){
              if(data.action == "append"){
                if (data.tool == "gen_ppt_html") {
                  if (data?.ppt_index) {
                    if ( data.targetKey in targetEvent && targetEvent[data.targetKey]?.length >= data.ppt_index) {
                      // 根据指定的页码 ppt_index 更新html 内容
                      const ppt_index = data.ppt_index || 1;
                      targetEvent[data.targetKey][ppt_index-1] = (targetEvent[data.targetKey][ppt_index-1] || "") + data?.content || '';
                    }
                  }else {
                    targetEvent[data.targetKey] = (targetEvent[data.targetKey] || "") + data[data.targetKey];
                  }
                }else {
                  targetEvent[data.targetKey] = (targetEvent[data.targetKey] || "") + data[data.targetKey];

                  // 代码流式输出，特例，第一个节点拿不到 language
                  if(data.tool == "sandbox_coder" && !targetEvent.language && data.language){
                    targetEvent.language = data.language;
                  }
                  if(data.tool == "sandbox_coder" && !targetEvent.language2 && data.language2){
                    targetEvent.language2 = data.language2;
                  }
                }
              } else if(data.action == "replace"){
                targetEvent[data.targetKey] = data[data.targetKey];
              }

              // 已展开 html 节点，自动更新
              // 新版本不再有 html 节点
              // if(data.type == "html"){
              //   if(this.mcpStore.currentFileNode?.planStepId == data.planStepId){
              //     this.mcpStore.setCurrentFileNode(targetEvent);
              //   }
              // }

              persistMessage({ ...messageItem, last_event_id: id });
              return;
            } else {
              console.error(`未找到待更新节点 ${data.planStepId}`, data);
              isNewEvent = true;
              message = { events: [...events, ...[data]], loading: false };
            }
          } else {
            if (data.tool == "gen_ppt_html") {
              if (data?.html_count) {
                const html_count = data?.html_count || 1;
                if (targetEvent) {
                  // 为显示三级目录，先于 html_count消息前发送了一个空的 html 节点
                  targetEvent.contents = new Array(html_count).fill("");
                  return;
                }

                // 存在html_count字段，创建contents数组
                data.contents = new Array(html_count).fill("");
              }
            }
            isNewEvent = !events.some(event => event.planStepId == data.planStepId);
            message = { events: [...events, ...[data]], loading: false };
          }

          // tool 节点，自动更新右侧内容
          if(
            isNewEvent &&
            (
              (data.tool && McpToolsConfig[data.tool])
              || McpActionTypes.includes(data.type)
              || McpActionTools.includes(data.tool)
              || (
                ["toolNeedConfirm","extraData"].includes(data.type)
                && (data.status == "success" || message.events.filter(event => event.type == "planStep").length > 1)
              )
            )
          ){
            if(!this.mcpStore.isInvalidEvent(data) && !(data.tool == "aiso_do_search" && data.status == "success" )){
              this.mcpStore.setCurrentPanelId(data.planStepId);
            }
            setTimeout(() => {
              if(!this.mcpStore.hasAutoShowRightPanel && !this.mcpStore.isInvalidEvent(data)){
                this.mcpStore.setHasAutoShowRightPanel(true);
                this.mcpStore.setShowRightPanel(true);
              }
            }, 50);
          }

          // 附件 pdf 节点，自动展开 file viewer
          if(
            isNewEvent
            && data.type == "attachments"
            && (
              !globalStore.globalConfig.mcpHtmlGtAttachments
              || !["html"].includes(this.mcpStore.currentFileNode?.type)
            )
          ){
            const attachments = data.attachments || [];
            const fileItem = attachments.find(item => AutoPreviewAttachmentTypes.includes(item.contentType));
            if(fileItem){
              // this.mcpStore.setCurrentFileNode(fileItem);
              const id = getAttachmentsId(fileItem);
              this.mcpStore.setCurrentPanelId(id);
            }
          }

          if (data.type == "toolStatus" && data.status == "success") {
            // 记录手动生成 Agent 的时间
            runInAction(() => {
              this.manuGenAgentSet[data.tool] = new Date().getTime();
            });
          }
          break;
        }
        case 'agent_err':{
          message = { events: [{
            content: data,
            event: true,
            messageType: "text",
            type: "chat",
          }], loading: false };
          break;
        }
        case 'code_info':{
          const messageItem = this.messageMap.get(messageId) || {};
          message = {
            code_info : data,
          }
          slsLogger.log({
            key : "chat",
            action : "code_info",
            ext : JSON.stringify(data),
            conversation_id : this.chatId,
            message_id : messageItem.msg_id || "",
          });
          break;
        }
      }
      message.last_event_id = id;
      this.updateMessage(messageId, message);
    } catch {}
  };

  handleMessage = (messageId, msg, body) => {
    // console.log(`🚀 ~ onmessage:`, msg);

    const { id, event, data, cost } = msg;
    const code = parseInt(event, 10);

    let status = 1;
    const messageItem = this.messageMap.get(messageId) || {};
    let { result = '', role_biz = '', reasoning_text = '' } = messageItem;
    switch (code) {
      case 100:
        this.#issuedChatId = data.replace('CONVERSATIONID####', '').trim();
        HelperCore.log(`生成 cid: ${this.#issuedChatId}`);
        chatStoreManager.updateConversationId(this.#issuedChatId, body);

        const now = +new Date;
        slsLogger.log({
          key : "performance",
          action : "cid_time",
          count : now - this.stepsLogger.beginTime,
          ext3 : HelperCore.copyObj(body, [
            "role",
            "ai_agent_id",
            "ai_agent_flow_id",
            "agent_id",
            "role_biz",
          ]),
          query : body.prompt,
          conversation_id : this.#issuedChatId,
        });
        return;
      case 101:
        // 第一条消息，打 show 点
        if(!this.messageList.length || this.messageList.every(item => checkFEMessageId(item.msg_id))){
          this.log({
            key: "aibot_chat",
            action: "show",
            ext: "new",
          });
        }

        const issuedMessageId = data.replace('MESSAGEID####', '').trim();
        if (msg.refer_search?.length) {
          this.updateMessage(messageId, {
            refer_search: msg.refer_search,
          })
        }
        if (msg.docs?.length) {
          this.updateMessage(messageId, {
            user_knowledge: msg.docs,
          });
        }
        if (issuedMessageId !== messageId) {
          const updateData = {
            msg_id: issuedMessageId,
            last_event_id: Number(id),
            loading: !(role_biz == "mind" ||  role_biz == "page_mind"),
          };
          if (body.html_message_id || body.parent_msg_id) {
            updateData.parent_msg_id = body.html_message_id || body.parent_msg_id;
          }
          if (body.generate_type === 6) {
            updateData.generate_type = 6
          }
          this.updateMessage(messageId, updateData);
        }

        // 每次发送消息，如果有文件，轮询文件解析状态
        if (this.file && this.file.length > 0) {
          // 先停用所有轮询
          fileStore.clearAllRecursive()
          // 启动轮询
          const callback = fileStore.getFilesParsingAsync(this.#issuedChatId, issuedMessageId, this.file)
          const recursiveFunction = fileStore.createRecursiveFunction(
            callback,
            this.#issuedChatId,
          )
          recursiveFunction()
        }

        this.canAborting = true;
        if(
          this.mcpStore.isMcpAgent
          || this.lastMessage.role_biz == "ai_agent"
          || this.mcpStore.isAgentFlow
          || this.lastMessage.role_biz == "ai_agent_flow"
        ){
          this.mcpStore.setPanelMessageId(issuedMessageId);
        }
        this.log({
          ...chatStoreManager.newChatLogData,
          ...{
            key : "aibot_chat",
            action : "act",
            arrt : "new_chat",
            message_id : issuedMessageId,
          },
        });

        return;
      case 102:
        this.canAborting = true;

        // this.updateMessage(messageId, { last_event_id: Number(id) });
        this.handleSearchMessage(messageId, data, Number(id));
        return;
      case 200:
        if(!result){
          this.stepsLogger.record("first_response");
        }
        result += data;
        if (cost) {
          this.updateMessage(messageId, { cost });
        }
        // HTML 自动预览
        if(
          // 有可能输出 html 后不再有正文
          // this.mcpStore.hasHtmlFinished
          messageItem.code_info?.title
          && result.split('```').length >= 3
          && !messageItem.autoPreview
          && messageItem.msg_id == this.mcpStore.currentFileNode?.messageId
          && !this.mcpStore.isHtmlPreview
        ){
          this.updateMessage(messageId, { autoPreview: true });
          this.mcpStore.setHtmlPreview(true);
        }
        break;
      case 201: // 文生图
        result = data;
        this.updateMessage(messageId, {
          canAborting: true,
          isAITyping: true,
          status,
          result,
          tti_image: result,
          last_event_id: Number(id),
        });
        return;
      case 40042:
        status = -1;
        if(!isStringEmpty(data)){
          result = data;
        }
        slsLogger.log({
          key : "chat",
          action : "error",
          arrt : code,
          ext : JSON.stringify(msg),
        });
        if(this.mcpStore.showRightPanel){
          this.mcpStore.setShowRightPanel(false);
        }
        break;
      case 40043:
        status = -1;
        this.updateMessage(messageId, {
          openCode: code,
        });
        if (!isStringEmpty(data)) {
          result = data;
        }
        slsLogger.log({
          key : "chat",
          action : "error",
          arrt : code,
          ext : JSON.stringify(msg),
        });
        break;
    }

    this.updateMessage(messageId, {
      loading: false,
      typing: true,
      status,
      result,
      last_event_id: Number(id),
    });
  };

  getModelType = (model) => {
    if (model.chat_type === 100) {
      if(model.model_type === 3) {
        return 3
      }
      return 2
    }
    return 1
  }

  handleCompleted = async (virtualMessageId, message, body, is_so, _role_id) => {
    if (!this.messageMap.has(virtualMessageId)) {
      return;
    }

    console.log(`🚀 ~ handleCompleted:`, this.lastMessage);

    runInAction(() => {
      this.quitQueue();
    });

    const msgObj = this.messageMap.get(virtualMessageId);
    const {
      msg_id,
      prompt,
      role_id,
      reasoning_text = '',
      reasoning_duration = '',
      result,
      user_knowledge = [],
      dataset,
      dataset_src,
      file,
      refer_search,
      search_keyword,
      search_total,
      annex_msg_id,
      ks_id,
      cost,
      code_info,
    } = msgObj;

    // HTML 自动预览
    // if(
    //   msgObj.events?.length
    //   && msgObj.events.find(item => item.type == "html")
    //   && this.mcpStore.fixedFileNode?.type == "html"
    //   && !msgObj.autoPreview
    // ) {
    //   HelperCore.log(`HTML 自动预览`);
    //   this.updateMessage(virtualMessageId, {
    //     autoPreview : true,
    //   });
    //   this.mcpStore.setHtmlPreview(true);
    // }

    const _cost = {
      time : Math.floor((+new Date - this.stepsLogger.beginTime) / 1000),
      token : Math.ceil(
        (
          (result?.length || 0)
          + (reasoning_text.length || 0)

          // MCP 如何计算？太少了
          // 输出速度: 6 tokens/s
          // + (msgObj.events?.length ? JSON.stringify(msgObj.events).length : 0)
        ) / 1.7),
    }
    if (cost?.speed) {
      _cost.speed = cost.speed
    }
    cacheStore.setCostTime(msg_id, cost);
    this.handleIssuedMessageId(virtualMessageId, {
      loading: false,
      typing: false,
      is_doing: 0,
      result: (result || '') + '\n',
      cost: _cost,
      ...message,
    });

    this.setAITyping(false);

    // html 数据有效性校验
    if(code_info?.title && result.split('```').length != 3){
      slsLogger.log({
        key : "html",
        action : "result_error",
        ext : result,
        query : prompt,
        conversation_id : this.chatId,
        message_id : msg_id,
      });
    }
    // 智能体 html 数据有效性校验
    if(msgObj.events?.length){
      if(msgObj.events.filter(item => item.type == "html").length > 1){
        slsLogger.log({
          key : "agent",
          action : "html_error",
          ext : JSON.stringify(msgObj.events.filter(item => item.type == "html")),
          query : prompt,
          conversation_id : this.chatId,
          message_id : msg_id,
        });
      }
    }
    // 智能体 events 数据有效性校验
    if(
      (this.aiAgentId && this.mcpStore.isMcpAgent)
      || (this.aiAgentFlowId && this.mcpStore.isAgentFlow)
    ){
      if(!msgObj.events?.length){
        slsLogger.log({
          key : "agent",
          action : "events_error",
          ext : JSON.stringify(msgObj),
          query : prompt,
          conversation_id : this.chatId,
          message_id : msg_id,
        });
      }
      else {
        const lastEvent = msgObj.events[msgObj.events.length - 1];
        if(lastEvent.status == "running"){
          slsLogger.log({
            key : "agent",
            action : "events_status_error",
            ext : JSON.stringify(lastEvent),
            query : prompt,
            conversation_id : this.chatId,
            message_id : msg_id,
          });
        }
      }
    }

    removeChatStorageItem(msg_id);

    this.stepsLogger.record('reply_finished', false, 'ext');

    if (
      !isStringEmpty(this.#issuedChatId) &&
      this.chatId !== this.#issuedChatId
    ) {
      this.chatId = this.#issuedChatId;
      if (this.firstMessage.msg_type === MessageType.Welcome) {
        this.messageMap.delete(this.firstMessage.msg_id);
      }
    }

    // 本地会话数据上传!
    if (client.isLLQ) {
      // console.log('>>>本地会话数据上传');
      const model = navbarStore.chatModels[role_id];
      const {
        localModel,
        apply_model,
        chat_type,
      } = model || {};
      const {
        title
      } = this.__historyData;
      if (localModel || chat_type === 100) {
        await request.post("/batch/add/message", {
          messages: JSON.stringify([{
            role_id,
            role_biz: file && file.length ? 'doc' : 'common',
            file: file ? JSON.stringify(file) : '',
            refer_search: refer_search ? JSON.stringify(refer_search) : '',
            search_keyword: search_keyword ? JSON.stringify(search_keyword) : '',
            search_total,
            conversation_id: this.chatId,
            title: title || prompt,
            prompt,
            content: result,
            reasoning_text,
            reasoning_duration: reasoning_duration.toString(),
            msg_id,
            model_type: this.getModelType(model), // TODO
            apply_model,
            version: 1,
            kwargs: {
              icon: model?.icon,
              name: model?.title,
            },
            user_knowledge: JSON.stringify(user_knowledge),
            dataset: JSON.stringify(dataset),
            dataset_src,
            annex_msg_id,
            ks_id,
          }]),
          agent_id: agentStore?.agent_id,
        }).catch(res => {
          // res.msg && Other.toast(res.msg, "error");
        })
      }
    }

    setTimeout(() => {
      navbarStore.getHistory(true, globalStore.isAgentPage() ? "6" : "5");
      sendBroadcast('history-changed');
    }, 1000);

    this.#emitCompletion();
    // 传入参数 ：用户的UID、用户的搜索结果、模式广场（用户使用的那种模型）、设备、系统、下图的Tab分类（现无埋点，待接入埋点后，在将该字段传输至问卷）字段
    const curTabInfo = globalStore.cloudConfig?.assistantAbilityTabList?.find(item => item.text == body?.prompt);
    userStore.qaSDKTrigger({
      prompt: body?.prompt || '',
      msg_id,
      model_type : navbarStore.chatModels[_role_id]?.title || '',
      tab_type: curTabInfo?.category || '',
      is_so,
      platform : 'web',
      ua : navigator.userAgent,
      search_status: message?.status != -1 ? 'success' : 'fail'
    });
  };

  sendMessage = async (params) => {
    debugChat("sendMessage");
    this.hasOrderQueueNotice = false;
    return this.mcpStore.checkMcpCondition({ params, callback: this.sendMessageAction })
  }

  sendMessageAction = async ({
    asstId = this.currAsstId,
    chatId = this.chatId,
    messageId = undefined,
    html_message_id = undefined,
    agentId = this.agentId,         // 中台智能体
    aiAgentId = this.aiAgentId,     // Mcp 智能体
    aiAgentFlowId = this.aiAgentFlowId, // 工作流智能体
    input = '',
    plan_id,
    plan_message_id,
    renew = false,
    retry = false,
    last_event_id = 0,
    page,
    summary,
    compare_parent_id = '',
    role_biz = '',
    rewrite_type = "",
    txt,
    translate_type,
    file,
    kwargs,
    ks_id = this.lastMessage?.ks_id,
    dataset,
    dataset_src,
    dataset_info,
    payload,
    firm_id = '',
    is_filter,
    field_category,
    intentRes,
    kn_continue_chat, // 分享的知识库继续聊天
    entrance,
    generate_type,
    parent_msg_id,
    events,
    ctx_extra_id,
    from_aiso,
    ...resetOptions
  }) => {
    entrance = Number(entrance || 0);
    this.canAborting = false;
    this.file = file
    this.setAITyping(true);
    this.setCurrAsstId(asstId);

    if(!this.__historyData?.title || this.__historyData.title == Loader.defaultChatTilte){
      this.__historyData = {
        ...this.__historyData,
        ...{
          title : input,
        },
      }
    }
    // 发送消息，是自己的会话
    if(!this.__historyData.qid){
      this.__historyData.qid = userStore.uid;
    }
    modelChoiceStore.updateCacheId(asstId);
    //只有助手支持文件上传
    if(asstId != Conf.roleIds.assistant && !inputTypeStore.supportUploadFile){
      file = [];
    }
    if (file && file.length > 0) {
      // 更新云控的文件解析文案映射
      fileStore.updateAssistantChatDocParams(globalStore.cloudConfig.assistantChatDocParams)
    }

    let annex_msg_id = getAnnexMsgId(this.lastMessage);
    if (this.isTtiModel) {// 文生图模型
      role_biz = 'draw'
      !input.startsWith('帮我画图：') && (input = `帮我画图：${input}`)
      annex_msg_id = ''
    }
    const is_so = this.isRecommendAgent || this.aiAgentId || this.aiAgentFlowId ? false : globalStore.robotOnlineSo(this.currAsstId) // 是否联网搜索

    let role_mode = globalStore.robotDeepSo(this.currAsstId)
    role_mode = role_mode ? 0 : 1

    generate_type = generate_type ?? (globalStore.robotDynamicPageSo(this.currAsstId) ? 5 : 0);
    if (navbarStore.isDSModel(this.currAsstId)) {
      kwargs = {think_stream: true, ...kwargs}
    }
    if(this.agentId) {
      let basic_info = this.agentInfo?.basic_info || {}
      let knowledge = basic_info.knowledge;
      let datasetId = Array.isArray(knowledge) ? knowledge.length && knowledge[0]?.folder_id : knowledge?.folder_id
      if(datasetId) {
        dataset = {[datasetId]:[]}
      }
      if(this.agentInfo.agent_type === 'my') { // 个人创建的agent，走我们
        asstId = basic_info?.llm_model?.id
      }else {
        role_biz = 'agent'
      }
    }
    if(aiAgentId){
      role_biz = 'ai_agent';

      try{
        const agentInfo = this.mcpStore.agentInfo;
        if(!dataset && agentInfo.dataset_id){
          dataset = { [agentInfo.dataset_id] : [] }
        }
        if(!asstId && agentInfo.role_id){
          asstId = agentInfo.role_id;
        }

        this.__historyData.ai_agent_type = agentInfo.agent_type;
      } catch(e){}

      // this.mcpStore.setCurrentFileNode(null);
      // this.mcpStore.setHtmlPreview(false);
      // this.mcpStore.setShowRightPanel(false);
      // this.mcpStore.setCurrentPanelId(null);
    }
    if(aiAgentFlowId){
      role_biz = 'ai_agent_flow';
    }

    // 选择的mcp工具列表
    const mcpServeObj = serverChoiceStore.getMcpServeParams(asstId);
    let mcpServeParams = {}
    if (mcpServeObj?.cloud_mcp?.length) {
      mcpServeParams = {
        cloud_mcp: mcpServeObj.cloud_mcp,
        role_biz: "ai_agent",
      }
    }

    // 第一条消息，生成对话总结标题
    const generate_title = this.messageList.length <= 1 && (!this.firstMessage || this.firstMessage.msg_type == MessageType.Welcome);

    const messageItem = await this.appendMessage({
      asstId: asstId,
      chatId,
      messageId,
      agentId,
      input,
      plan_id,
      plan_message_id,
      page,
      renew,
      compare_parent_id,
      role_biz,
      rewrite_type,
      txt,
      translate_type,
      file,
      annex_msg_id,
      kwargs,
      ks_id,
      dataset,
      dataset_src,
      dataset_info,
      payload,
      is_so,
      is_filter,
      field_category,
      intentRes,
      kn_continue_chat,
      entrance,
      role_mode,
      generate_type,
      events,
      ...mcpServeParams,
      ...resetOptions,
    });

    const virtualMessageId = messageItem.msg_id;
    const body = {
      ver_num: 2,
      role: asstId,
      conversation_id: kn_continue_chat ? '' : chatId, // 别人分享的知识库继续聊天，不能带cid
      message_id: retry ? messageId : undefined,
      parent_msg_id: parent_msg_id, // 生成网页重试
      html_message_id,
      agent_id: agentId,
      ai_agent_id: aiAgentId,
      ai_agent_flow_id: aiAgentFlowId,
      prompt: input,
      plan_id,
      plan_message_id,
      re_answer: renew ? 1 : 0,
      re_answer_msg_id: renew ? messageId : undefined,
      retry,
      last_id: last_event_id,
      page,
      compare_parent_id,
      summary,
      role_biz,
      firm_id,
      rewrite_type,
      txt,
      file,
      payload,
      annex_msg_id : messageItem.annex_msg_id,
      kwargs: getKwargs(kwargs),
      ks_id,
      dataset,
      dataset_src,
      dataset_info,
      is_so,
      // role_mode: role_mode ? 0 : 1,
      role_mode,
      generate_type,
      is_filter,
      field_category,
      intentRes,
      kn_continue_chat,
      entrance,
      generate_title,
      ctx_extra_id,
      from_aiso : from_aiso ?? (this.historyData.from_aiso || 0),
      ...mcpServeParams,
      ...generateCustomAPIParams(file)
    };

    EE.emit('send_message_sucess');

    if(
      this.mcpStore.isMcpAgent
      || this.mcpStore.isAgentFlow
      || mcpServeObj.local_mcp?.length
    ){
      try{
        const serverList = this.mcpStore?.agentInfo?.server_list;
        let serverNames = [];
        if (serverList?.length > 0) {
          serverNames = serverList.filter(item => item.is_local == 1).map(item => item.server_name);
        } else if (mcpServeObj.local_mcp?.length) {
          serverNames = mcpServeObj.local_mcp
        }

        if(serverNames.length){
          body.serverNames = serverNames;
          body.isAgentFlow = this.mcpStore.isAgentFlow;

          // const mcp = await clientMcp.getMcpInfos(serverNames);
          // if(mcp && Array.isArray(mcp)){
          //   body.mcp = mcp;
          //   if (mcpServeObj.local_mcp?.length && mcp.length) {
          //     const role_biz = this.mcpStore.isAgentFlow ? "ai_agent_flow" : "ai_agent";
          //     body.role_biz = role_biz;
          //     this.updateMessage(virtualMessageId, {
          //       mcp,
          //       role_biz,
          //     });
          //   }
          // }
        }
      } catch(e){}

      body.kwargs = {
        ...body.kwargs,
        ...{
          vip : 1,
        },
      }

      if(!this.aiAgentFlowId){
        this.orderQueue();
      }
      this.mcpStore.setHasAutoShowRightPanel(false);
      this.mcpStore.setHasSelectedPanelId(false);
    }
    try{
      if(this.mcpStore.agentInfo.agent_type){
        body.agent_type = this.mcpStore.agentInfo.agent_type;
      }
    } catch(e){}
    if(this.__historyData.kn_custom_model){
      const kn_custom_model = this.__historyData.kn_custom_model;
      body.kn_custom_model = kn_custom_model;
      // 非 r1 不支持深度
      if(kn_custom_model.deep != "r1" || !globalStore.getRobotDeepSet(Conf.roleIds.knowledge)){
        body.role_mode = 1;
      }
    }

    this.#chatController = new AbortController();
    this.stepsLogger = new StepsLogger();

    inputTypeStore.reset();
    createCompletion({
      body,
      signal: this.#chatController.signal,
      onhookBody : (body) => {
        const logData = {
          action : "chat_send",
          query : body.prompt,
          ext1 : body.role_biz,
          ext2 : getMessageLogData(body),
        };
        if(this.aiAgentId){
          logData.ext3 = JSON.stringify(this.mcpStore.agentInfo);
        }
        slsLogger.log(logData);

        if(body.from_aiso && !this.historyData.from_aiso){
          runInAction(() => {
            this.__historyData = {
              ...this.__historyData,
              ...{
                from_aiso : body.from_aiso,
              },
            }
          })
        }
        if(body.mcp && body.role_biz){
          runInAction(() => {
            this.updateMessage(virtualMessageId, {
              mcp : body.mcp,
              role_biz : body.role_biz,
            });
          })
        }
      },
      onopen: async (res, format = 'stream') => {
        setTimeout(() => {
          userStore.getCoinBalance();
          userStore.getMembershipInfo();
          userStore.getMembershipUsage();
        }, 800)

        try {
          let json;
          if (format === 'stream') {
            if (res.headers.get('content-type')?.includes('application/json')) {
              json = await res.clone().json();
            }
          } else if (format === 'json') {
            json = res;
          }

          if (json) {
            const { code, msg } = json;
            switch(code){
              case 100010:{
                Other.toast("请先登录", "info");
                userStore.gotoLogin();
                break;
              }
              case 100011:{
                BanModal.show();
                globalStore.gotoHome();
                break;
              }
              case 110031:
              case 110032:
              case 110033:{
                slsLogger.log({
                  action : "sign_method",
                  query : body.prompt,
                  ext1 : code,
                  ext2 : getMessageLogData(body),
                });
                break;
              }
              case 110034:{
                // 签名算法过期，需要刷新页面
                slsLogger.log({
                  action : "sign_method",
                  query : body.prompt,
                  ext1 : code,
                  ext2 : getMessageLogData(body),
                });
                // 20241204 发现有大量 110034，可能和切域名有关，先不刷新，等服务端修复后考虑放开
                // setTimeout(() => {
                //   globalStore.reloadApp();
                // }, 30);
                break;
              }
              case 400011:{
                // alert("免费次数已用尽->跳转开通会员")
                limitAlertStore.setData({
                  show: true,
                  type: 1
                })
                break;
              }
              case 400012:{
                // alert("纳米币余额不足->充值")
                limitAlertStore.setData({
                  show: true,
                  type: 6
                })
                break;
              }
              case 400013:{
                // alert("并发超过限制->非会员 跳转开通会员")
                limitAlertStore.setData({
                  show: true,
                  type: 2
                })
                break;
              }
              case 400017:{
                // alert("纳米币余额不足->开会员充值")
                limitAlertStore.setData({
                  show: true,
                  type: 3
                })
                break;
              }
              case 300104:{
                // alert("并发超过限制->会员 展示错误")
                limitAlertStore.setData({
                  show: true,
                  type: 5
                })
                break;
              }
              // 切换智能体，新开会话
              case 420300:{
                const data = json.data;
                const { ai_agent_id, intent_id } = data;
                const {
                  prompt,

                  // 新会话排除的参数
                  role,
                  conversation_id,
                  message_id,
                  parent_msg_id,
                  agent_id,
                  ai_agent_id: _ai_agent_id,
                  re_answer,
                  re_answer_msg_id,
                  last_id,
                  retry,

                  ...bodyParams
                } = body;
                bodyParams.kwargs = bodyParams.kwargs || {};
                bodyParams.kwargs.intent_id = intent_id;

                // 清除当前消息
                this.messageMap.delete(virtualMessageId);
                // 清除左侧会话
                if(!this.messageMap.size){
                  navbarStore.deleteHistoryItem(body.conversation_id);
                }

                urlStore.newAiAgentChat(ai_agent_id, prompt, bodyParams);
                return;
              }
              default:{
                slsLogger.log({
                  action : "chat",
                  arrt : "json",
                  query : body.prompt,
                  ext1 : JSON.stringify(json),
                  ext2 : getMessageLogData(body),
                });
                break;
              }
            }
            let errorMessage =
              RESPONSE_ERROR_MESSAGE[code] || msg || SERVICE_ERROR_MESSAGE;

            if (code === 400011) { // 追加链接
              errorMessage += ' [去邀请好友助力](https://bot.n.cn?from=chat_invite)';
            }

            this.updateMessage(virtualMessageId, {
              status: -1,
              openCode: code,
              loading: false,
              result: errorMessage,
            });
          }
        } catch {}
      },
      onmessage: msg => {
        // debug
        // HelperCore.log(`onmessage`, msg);

        try {
          this.handleMessage(virtualMessageId, msg, body);
        } catch (error) {
          console.error(`onmessage ==========>`, error);
        }
      },
      onclose: () => {
        const lastMessage = this.messageMap.get(virtualMessageId) || {};
        const { msg_id, result, status, role_id, mind_map } = lastMessage;
        const isMcpServer = lastMessage?.cloud_mcp?.length > 0 || lastMessage?.mcp?.length > 0;
        const isEmpty = isStringEmpty(result) && !mind_map && !(this.mcpStore.isMcpAgent || this.mcpStore.isAgentFlow || isMcpServer);

        const succ = !isEmpty && status != -1;
        this.handleCompleted(
          virtualMessageId,
          isEmpty ? { status: -1, result: ABORT_MESSAGE } : {},
          body,
          is_so,
          this.currAsstId
        );
        if(succ){
          const asstInfo = navbarStore.chatModels[role_id];
          modelHistoryStore.frontItem(asstInfo);
          aigentHistoryStore.getAigentList()
          const logData = this.log({}, true);
          const ext2 = logData.ext2;
          delete(logData.ext2);
          this.stepsLogger.done(
            msg_id,
            succ,
            {
              arrt : this.lastChatType,
              ext4 : result?.length || 0,
              ext6 : ext2,
              message_id : msg_id,
              ...logData,
            },
          );
          setTimeout(() => {
            userStore.getCoinBalance();
            userStore.getMembershipInfo();
            userStore.getMembershipUsage();
          }, 800)
          // 智能体，资源紧张
          if(this.mcpStore.isAgent && (lastMessage.events || []).find(item => item.type == "agentStatus" && item.success === false)){
            this.log({
              action : "act",
              arrt : "message_fail",
              ...getFailMessageParams(lastMessage),
              message_id : msg_id,
            });
          }
          else {
            this.log({
              action : "act",
              arrt : "message_success",
              message_id : msg_id,
            });

            if(
              this.hasOrderQueueNotice
              // && !(lastMessage.events || []).find(item => item.type == "clarification")
              && (lastMessage.events || []).find(item => item.type == "attachments")
            ){
              runInAction(() => {
                this.hasOrderQueueNotice = false;
                if(noticeStore.permission){
                  noticeStore.showMessage({
                    title : "任务已完成",
                    onClick : () => {
                      globalStore.navigate(`/chat/${this.chatId}`);
                    },
                  });
                }
              })
            }
          }
        }
        else {
          this.log({
            action : "act",
            arrt : "message_fail",
            ...getFailMessageParams(lastMessage),
            message_id : msg_id,
          });
        }
      },
      onerror: err => {
        console.error(`onerror ==========`, err);
        const is_retry = err?.name === 'TypeError';
        const lastMessage = this.messageMap.get(virtualMessageId);
        const { msg_id, role_id, prompt } = lastMessage;
        slsLogger.log({
          action : "chat_error",
          arrt : "error",
          query : prompt,
          ext : this.chatId,
          ext1 : err?.name || "unknown",
        });
        let result = is_retry ? NETWORK_ERROR_MESSAGE : UNKNOW_EXCEPTION_MESSASGE;
        if (!Number.isNaN(+err) && err in CHAT_ERROR_MESSAGE) {
          result = CHAT_ERROR_MESSAGE[err];
        }
        this.handleCompleted(virtualMessageId, {
          status: -1,
          is_retry,
          result,
        }, body);
        appLogger.log({
          key : "aibot_plugin_public",
          action : "message_fail",
          arrt : this.lastChatType,
          ...getFailMessageParams(lastMessage),
          role_id,
          message_id : msg_id,
          conversation_id : this.chatId,
        });
      },
    }).finally(() => {
      this.#chatController = null;
    });
  };

  deleteMessage = async messageId => {
    const handleDelete = () => {
      if (this.messageMap.has(messageId)) {
        runInAction(() => {
          this.messageMap.delete(messageId);
          // 如果上一条消息是清空上下文消息，需要我们一并删除它
          if (this.messageList.length > 1) {
            let index = this.messageList.findIndex(x => x.msg_id === messageId);
            let preItem = this.messageList[index - 1];
            if (index != -1 && preItem?.msg_type == MessageType.ClearContext) {
              this.messageMap.delete(preItem.msg_id);
            }
          }
        });
      }
    };

    if (checkFEMessageId(messageId)) {
      handleDelete();
      return 0;
    }

    const { code } = await removeMessage(this.chatId, messageId);
    if (code == 0) {
      handleDelete();
    }
    return code;
  };

  clearMessages = async () => {
    const { code } = await removeAllMessages(this.chatId);
    if (code == 0) {
      this.messageMap.clear();
    }
  };

  abort = async (callback) => {
    // debug
    HelperCore.log('chat abort');

    runInAction(() => {
      this.quitQueue();
    });

    if (this.canAborting && this.isAITyping) {
      this.log({
        action : "click",
        set : "stop",
      });

      let state = 0;
      if (navbarStore.chatModels[this.currAsstId]?.localModel) {
        localRobot.stop();
      } else if (navbarStore.chatModels[this.currAsstId]?.chat_type === 100) {
        apiRobot.stop()
      } else {
        const { code } = await abortEventStream(
          this.#issuedChatId || this.chatId,
          this.lastMessage.msg_id
        );
        state = code;
      }

      if (state == 0) {
        callback && callback();
        this.setAITyping(false);

        const virtualMessageId = this.lastMessage.virtual_message_id;
        this.handleIssuedMessageId(virtualMessageId, {
          loading: false,
          typing: false,
          is_doing: 0,
        });
        runInAction(() => {
          this.isAITyping = false;
        });
      }
      else {
        slsLogger.log({
          action : "chat",
          arrt : "stop_error",
          query : this.lastMessage.prompt,
          ext : state || '',
          conversation_id : this.#issuedChatId,
          message_id : this.lastMessage.msg_id,
        });
      }
    }
  };

  onCompletion = listener => {
    this.#emitter.addListener('completion', listener);
    return () => {
      this.#emitter.removeListener('completion', listener);
    };
  };

  #emitCompletion = () => {
    this.#emitter.emit('completion', { chatId: this.chatId });
  };

  fetchChatHeaderInfo = (conversation_id, notSetLoading) => {
    if (!conversation_id) return;
    if(this.chatHeaderInfo) return;
    runInAction(() => {
      //已经有默认的icon信息了，如果为true会闪一下
      if(!notSetLoading){
        this.isLoadingHeaderInfo = true;
      }
    });
    getChatHeaderInfo({
      conversation_id,
    })
    .then(res => {
      runInAction(() => {
        const list = res.data?.list || [];
        if (list.length) {
          this.chatHeaderInfo = {
            id: list[0].id,
            count: list.length,
          };
        }
      })
    })
    .finally(() => {
      runInAction(() => {
        this.isLoadingHeaderInfo = false;
      });
    });
  };

  /**
   * 发送解释、翻译消息
   * @param {String} type
   * @param {String} content
   */
  handleSlipperySearch = async (type, content, onlySetQuery) => {
    switch(type){
      // 解释
      case "1":{
        this.sendMessage({
          asstId : this.currAsstId || Conf.roleIds.deepseek,
          input : "解释",
          txt : {
            content : content.slice(0, Loader.messageLimit),
          },
          role_biz : "explain",
        });
        break;
      }
      // 翻译
      case "2":{
        this.sendMessage({
          asstId : this.currAsstId || Conf.roleIds.deepseek,
          input : "翻译",
          txt : {
            content : content.slice(0, Loader.messageLimit),
          },
          role_biz : "translation",
        });
        break;
      }
      // 划词搜索
      case "3":{
        if (onlySetQuery) {
          if (!this.currAsstId) {
            this.currAsstId = Conf.roleIds.deepseek;
          }
          EE.emit('set_query', content.slice(0, Loader.messageLimit));
        } else {
          this.sendMessage({
            asstId : this.currAsstId || Conf.roleIds.deepseek,
            input: content.slice(0, Loader.messageLimit),
          });
        }
        break;
      }
    }
  }

  /**
   * 接口拉取思维导图
   * 时机: 正文结束后
   */
  loadMindmap = (msg_id) => {
    const message = this.messageMap.get(msg_id);
    if(!isStringEmpty(message?.mind_map) || message?.mind_map === false){
      return;
    }
    if(this.loadingVideoMindMap){
      return;
    }
    this.loadingVideoMindMap = true;
    const options = {
      message_id : msg_id,
      conversation_id: this.chatId,
      regen : 0,
    };
    request.post("/wenku/resource/common/mind_map", options)
    .then((res) => {
      const { code, data } = res;
      const value = data?.content || "";
      this.updateMessage(msg_id, { mind_map: value });
    })
    .catch(res => {
      res.msg && Other.toast(res.msg, "error");
      this.updateMessage(msg_id, { mind_map: false });
    })
    .finally(() => {
      runInAction(() => {
        this.loadingVideoMindMap = false;
      });
    });
  }

  // MCP 分享回放
  mcpReplyIndex = {
    messageIndex : 0,
    eventIndex : 0,
  }
  setHistoryData (historyData) {
    this.__historyData = {
      ...this.__historyData,
      ...historyData,
    }
    if(historyData.ai_agent_id){
      this.aiAgentId = historyData.ai_agent_id;
      const {
        ai_agent_id,
        ai_agent_info,
      } = historyData;
      this.mcpStore.setAgentID(ai_agent_id, ai_agent_info && typeof ai_agent_info == "object" && !Array.isArray(ai_agent_info) ? ai_agent_info : null);
    }
    else if(historyData.ai_agent_flow_id){
      this.aiAgentFlowId = historyData.ai_agent_flow_id;
      const {
        ai_agent_flow_id,
        ai_agent_flow_info,
      } = historyData;
      this.mcpStore.setAgentID(ai_agent_flow_id, ai_agent_flow_info && typeof ai_agent_flow_info == "object" && !Array.isArray(ai_agent_flow_info) ? ai_agent_flow_info : null);
    }
  }
  appendMcpMessage (messageId, messageItem) {
    if(this.messageMap.has(messageId)){
      this.updateMessage(messageId, messageItem);

      this.mcpStore.setPanelMessageId(messageId);
      const events = messageItem.events;
      const lastEvent = events[events.length - 1];
      if(lastEvent){
        const data = lastEvent;

        // tool 节点，自动更新右侧内容
        if(
          (data.tool
          && McpToolsConfig[data.tool])
          || [
            ...McpActionTypes,
            "toolNeedConfirm",
            "extraData",
          ].includes(data.type)
          || McpActionTools.includes(data.tool)
        ){
          if(!this.mcpStore.isInvalidEvent(data)){
            this.mcpStore.setCurrentPanelId(data.planStepId);
          }
          if(!HelperCore.isPhone()){
            setTimeout(() => {
              if(!this.mcpStore.hasAutoShowRightPanel && !this.mcpStore.isInvalidEvent(data)){
                this.mcpStore.setHasAutoShowRightPanel(true);
                this.mcpStore.setShowRightPanel(true);
              }
            }, 50);
          }
        }

        // html 节点，自动展开 html viewer
        // 注意与流式输出时，处理有差异
        // 新版本不再有 html 节点
        // if(
        //   data.type == "html"
        //   && (
        //     !AutoPreviewAttachmentTypes.includes(this.mcpStore.currentFileNode?.contentType)
        //     || globalStore.globalConfig.mcpHtmlGtAttachments
        //   )
        //   && !HelperCore.isPhone()
        // ){
        //   this.mcpStore.setShowRightPanel(false);
        //   this.mcpStore.setCurrentFileNode(data);
        // }

        // 附件节点，自动展开 file viewer
        if(
          data.type == "attachments"
          && (
            !globalStore.globalConfig.mcpHtmlGtAttachments
            || !["html"].includes(this.mcpStore.currentFileNode?.type)
          )
          && !HelperCore.isPhone()
        ){
          const attachments = data.attachments || [];
          const fileItem = attachments.find(item => AutoPreviewAttachmentTypes.includes(item.contentType));
          if(fileItem){
            const id = getAttachmentsId(fileItem);
            this.mcpStore.setCurrentPanelId(id);
          }
        }
      }
    }
    else {
      this.messageMap.set(messageId, messageItem);
    }
  }
  replyMessages = [];
  timerReply = null;
  appendMcpMessages (messages, toEnd) {
    this.mcpReplyIndex = {
      messageIndex : 0,
      eventIndex : 0,
    }
    this.replyMessages = messages;
    // 直接结束
    if(toEnd){
      this.toggleReply();
    }
    else {
      this.mcpReply(true);
    }
  }
  mcpReply = () => {
    const messages = this.replyMessages;
    // 已结束
    if(messages.length <= this.mcpReplyIndex.messageIndex){
      HelperCore.log(`回放结束`);

      // HTML 自动预览
      const msgObj = messages[messages.length - 1];
      if(
        msgObj.events?.length
        && msgObj.events.find(item => item.type == "html")
        && this.mcpStore.fixedFileNode?.type == "html"
      ) {
        HelperCore.log(`HTML 自动预览`);
        this.mcpStore.setHtmlPreview(true);
      }
      return;
    }
    const message = messages[this.mcpReplyIndex.messageIndex];
    if(!message?.events?.length){
      return;
    }
    if(message.events.length < this.mcpReplyIndex.eventIndex){
      return;
    }

    // debug
    // HelperCore.log(`mcpReply`, HelperCore.copyObj(this.mcpReplyIndex), message.events.length);

    const lastEvent = message.events[this.mcpReplyIndex.eventIndex - 1];
    this.appendMcpMessage(message.msg_id, {
      ...message,
      ...{
        events : message.events.slice(0, this.mcpReplyIndex.eventIndex),
      },
    });
    ++this.mcpReplyIndex.eventIndex;

    // 无效节点，直接抛弃
    while(message.events[this.mcpReplyIndex.eventIndex] && this.mcpStore.isFilteredEvent(message.events[this.mcpReplyIndex.eventIndex])){
      ++this.mcpReplyIndex.eventIndex;
    }

    if(this.mcpReplyIndex.eventIndex > message.events.length){
      // 回放下一条消息
      this.mcpReplyIndex.eventIndex = 0;
      this.mcpReplyIndex.messageIndex++;
      this.mcpStore.setHasAutoShowRightPanel(false);
    }
    this.mcpReply();
  }
  toggleReply = () => {
    // 直达结束
      this.messageMap.clear();
      this.replyMessages.forEach(message => {
        this.mcpStore.getFixedEvents(message.events);
        this.messageMap.set(message.msg_id, message);
      });

      // 是否有 pdf 或 html
      // if(!HelperCore.isPhone()){
        this.mcpStore.setCurrentPanelId(null);
        // this.openLastCard();
      // }

      setTimeout(() => {
        this.scrollToBottom();
      }, 1000);
    // 重新回放
    // else {
    //   this.messageMap.clear();
    //   this.mcpStore.setHasAutoShowRightPanel(false);
    //   this.mcpStore.setShowRightPanel(false);
    //   this.mcpStore.setCurrentPanelId(null);
    //   this.mcpStore.setCurrentFileNode(null);
    //   this.mcpStore.setHasSelectedPanelId(false);
    //   this.appendMcpMessages(this.replyMessages, true);
    // }
  }
  scrollToBottom () {
    try{
      const $messageList = document.querySelector(".js-message-list");
      const scrollHeight = $messageList.scrollHeight;
      HelperCore.log(`scrollToBottom scrollHeight`, scrollHeight);
      $messageList.scrollTo(0, scrollHeight);
    } catch(e){}
  }
  openLastCard () {
    if(this.mcpStore.showRightPanel && this.mcpStore.currentPanelId){
      return;
    }

    const list = this.mcpStore.toolsNodes;
    if(!list?.length){
      return;
    }
    const lastNode = list[list.length - 1];
    if(!lastNode.planStepId || lastNode.msg_id != this.lastMessage.msg_id){
      return;
    }
    this.mcpStore.setShowRightPanel(true);
    this.mcpStore.setPanelMessageId(this.lastMessage.msg_id);
    this.mcpStore.setCurrentPanelId(lastNode.planStepId);
  }

  /**
   * 轮询排队
   */
  orderQueue () {
    this.queueCount = 0;
    clearTimeout(this.timerQueue);

    if(globalStore.cloudConfig.webConfig?.support_agent_queue != 1){
      return;
    }

    this.timerQueue = setTimeout(() => {
      this.rollQueue();

      // 开始轮询
      const params = {
        cid : this.#issuedChatId || this.chatId,
        ai_agent_id : this.aiAgentId,
      }
      slsLogger.log({
        key : "chat",
        action : "queue",
        ext : JSON.stringify(params),
      });
    }, this.timeQueueEmptyContent);
  }
  rollQueue () {
    clearTimeout(this.timerQueue);
    if(!this.isAITyping){
      return;
    }

    const params = {
      cid : this.#issuedChatId || this.chatId,
      ai_agent_id : this.aiAgentId,
    }
    request.get("/assistant/chat/queue", {
      params,
    })
    .then(res => {
      const { data } = res;
      const { is_waiting, waiting_count } = data;
      runInAction(() => {
        // 结束轮询
        if(!is_waiting && this.queueCount > 0){
          slsLogger.log({
            key : "chat",
            action : "queue_end",
            ext : JSON.stringify(params),
            ext1 : JSON.stringify(res),
            ext2 : this.queueCount,
          });
          this.quitQueue();
        }
        else {
          this.queueCount = waiting_count;
          this.timerQueue = setTimeout(() => {
            this.rollQueue();
          }, this.timeQueueRoll);
        }
      });
    })
    .catch(() => {});
  }
  quitQueue = () => {
    this.queueCount = 0;
    clearTimeout(this.timerQueue);
  }
  setHasOrderQueueNotice (hasOrderQueueNotice) {
    this.hasOrderQueueNotice = hasOrderQueueNotice;
  }

  setAiAgentId(aiAgentId) {
    // runInAction(() => {
      this.aiAgentId = aiAgentId;
      this.updateMessage(this.lastMessage.msg_id, {
        role_biz: "ai_agent"
      });
    // })
  }

  /**
   * 打点
   * @param {Object} params
   */
  log = (params = {}, forLogData) => {
    const commonParams = {
      role_id : this.currAsstId || '',
      agent_id : this.aiAgentId || '',
      conversation_id : this.#issuedChatId || this.chatId || '',
      chat_type : this.historyData.chat_type,
      conversation_title : this.historyData.summary_title || this.historyData.title,
      key : params.key || appLogger.getRouterName(),
      arrt: "aibot_chat_detail",
      page: "aibot_chat_detail",

      is_knowledge : false,
      knowledge_id : "",
    };
    const lastMessage = this.lastMessage;

    // 模型信息
    try{
      if(commonParams.role_id){
        const roleInfo = navbarStore.chatModels[commonParams.role_id];
        commonParams.role_name = roleInfo.title;
      }
    } catch(e){}

    // 智能体信息
    try{
      if(this.mcpStore.agentInfo){
        const { title, server_list } = this.mcpStore.agentInfo;

        commonParams.agent_name = title || "";
        commonParams.mcp_id = server_list.map(item => item.server_name).join(",");
        commonParams.mcp_name = server_list.map(item => item.display_name).join(",");
      }
    } catch(e){}

    // 知识库
    // try{
    //   const user_knowledge = lastMessage.user_knowledge;
    //   if(user_knowledge.length){
    //     commonParams.is_knowledge = true;
    //     commonParams.knowledge_id = HelperCore.removeRepeatArray(user_knowledge.map(item => item.folder_id)).join(",");
    //   }
    // } catch(e){}
    try{
      const dataset = lastMessage.dataset;
      if(dataset && Object.keys(dataset).length){
        commonParams.is_knowledge = true;
        commonParams.knowledge_id = Object.keys(dataset).join(",");
      }
    } catch(e){}

    // 联网、深度、AI动态等可选按钮
    try{
      const { is_so, role_mode, generate_type } = lastMessage;
      commonParams.is_online_search = !!is_so;
      commonParams.is_deep_search = role_mode == 0;
      commonParams.is_ai_dynamic = (generate_type == 5 || globalStore.robotDynamicPageSo(this.currAsstId)) && Loader.isDynamicPageRole(this.currAsstId);
    } catch(e){};

    // 其它
    try{
      commonParams.page_type = globalStore.isKownledgeChat() || this.historyData.dataset_src
        ? "knowledge_chat"
        : this.mcpStore.isAgent
        ? "aiagent_chat"
        : "model_chat";
    } catch(e){}
    try{
      commonParams.ext2 = !!(lastMessage.file?.length);
    } catch(e){}
    try{
      commonParams.ext = lastMessage.is_history ? "history" : "new";
    } catch(e){}

    const logData = {
      ...commonParams,
      ...params,
    };

    if(forLogData){
      return logData;
    }

    appLogger.log(logData);
  }
}

export default ChatStore;
